import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField, MenuItem, Grid, FormControlLabel, Switch, Button, Typography, CircularProgress, Box } from '@material-ui/core';
import InputMask from 'react-input-mask';

import { toggleShowDialog } from '../../store/actions/dialog';
import { addNegocio, updateNegocio, getNegocio, buscarSegmentosAtuacao, buscarPlanosAtivos } from '../../store/actions/negocio';
import { buscarEnderecoCEP } from '../../store/actions/endereco';
import TituloPagina from '../../components/layout/TituloPagina';
import validaCpfCnpj from '../../assets/js/validaCpfCnpj';
import LoadingOverlay from '../../components/layout/LoadingOverlay';
import If from '../../components/condicional/if';
import { getNumbers } from '../../assets/js/formatNumber';

const NegocioCadastro = props => {
    const { addNegocio, updateNegocio, getNegocio, negocio, buscarSegmentosAtuacao, segmentos, loading, processing, buscarPlanosAtivos, planosAtivos, user } = props;
    const history = useHistory();
    const location = useLocation();
    const [disableFields, setDisableFields] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState('');

    useEffect(() => {
        const id = Number(location?.state?.lojaId);
        const isEditRoute = location.pathname === '/lojas/editar-loja';

        if (isEditRoute && !id) {
            history.replace('/lojas');
            return;
        }

        if (id > 0) {
            getNegocio(id);
        }
    }, [getNegocio, history, location]);

    useEffect(() => {
        buscarSegmentosAtuacao();
        buscarPlanosAtivos();
    }, []);

    const validationSchema = yup.object({
        nome: yup.string()
            .required('O nome é obrigatório')
            .min(3, 'O nome do empreendimento deve ter entre 3 e 30 caracteres')
            .max(30, 'O nome do empreendimento deve ter entre 3 e 30 caracteres'),
        codigoTipo: yup.string()
            .required('Informe se é Pessoa Física ou Jurídica'),
        idSegmento: yup.string()
            .required('Informe o segmento de atuação do negócio'),
        cpfOuCnpj: yup.string()
            .test(
                'testCpfOuCnpj',
                'Informe um CPF/CNPJ válido',
                (cpfOuCnpj) => validaCpfCnpj(cpfOuCnpj)
            )
            .required('O CPF/CNPJ é obrigatório'),
        email: yup.string()
            .required('O e-mail é obrigatório')
            .email('Informe um e-mail válido'),
        telefonePrincipal: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .required('O telefone principal é obrigatório')
            .test('telefone-valid', 'Informe um telefone válido', (value) => {
                if (!value) return true;
                return /^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(value);
            }),
        contato: yup.string()
            .required('O contato é obrigatório'),
        cep: yup.string()
            .required('Informe o CEP do seu endereço'),
        logradouro: yup.string()
            .required('Informe o logradouro'),
        bairro: yup.string()
            .required('Informe o bairro'),
        cidade: yup.string()
            .required('Informe a cidade'),
        estado: yup.string()
            .required('Informe o estado'),
        numero: yup.string()
            .required('Informe o número'),
        planoId: yup.string()
            .required('Informe o Plano escolhido'),
    });

    const formik = useFormik({
        initialValues: {
            id: negocio?.id ?? '',
            nome: negocio?.nome ?? '',
            idSegmento: negocio?.idSegmento ?? '',
            cpfOuCnpj: negocio?.cpfOuCnpj ?? '',
            email: negocio?.email ?? '',
            telefonePrincipal: negocio?.telefonePrincipal ?? '',
            telefoneSecundario: negocio?.telefoneSecundario ?? '',
            contato: negocio?.contato ?? '',
            codigoTipo: negocio?.codigoTipo ?? '',
            instagram: negocio?.instagram ?? '',
            facebook: negocio?.facebook ?? '',
            dataCadastro: negocio?.dataCadastro ?? '',
            stripeSellerAccountId: negocio?.stripeSellerAccountId ?? '',
            ativo: negocio?.ativo ?? false,
            enderecoId: negocio?.enderecoId ?? '',
            cep: negocio?.cep ?? '',
            logradouro: negocio?.logradouro ?? '',
            numero: negocio?.numero ?? '',
            complemento: negocio?.complemento ?? '',
            bairro: negocio?.bairro ?? '',
            cidade: negocio?.cidade ?? '',
            estado: negocio?.estado ?? '',
            planoId: negocio?.planoId ?? '',
            planoDescricao: negocio?.planoDescricao ?? ''
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            setProcessingMessage('Salvando...');

            if (negocio.id > 0) updateNegocio(values);
            else addNegocio(values);

            setDisableFields(true);
            setProcessingMessage('');
        },
        enableReinitialize: true,
    });

    async function handleCep(e) {

        let cep = String(getNumbers(e.target.value));
        if (cep.length === 8) {
            setProcessingMessage('Buscando endereço...');
            setIsProcessing(true);

            const { logradouro, bairro, localidade, uf } = await buscarEnderecoCEP(cep);
            formik.setFieldValue('logradouro', logradouro);
            formik.setFieldValue('bairro', bairro);
            formik.setFieldValue('cidade', localidade);
            formik.setFieldValue('estado', uf);

            setIsProcessing(false);
            setProcessingMessage('');
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo='Cadastro de Loja' subtitulo={new Date().toLocaleString('pt-BR')} />
            <Grid container item xs={12} style={{ paddingTop: '15px' }}>
                <Grid container>
                    <Typography variant='body1' component='p'>
                        Informe abaixo os dados do empreendimento.
                    </Typography>
                </Grid>
                <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
                    <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    fullWidth
                                    id='nome'
                                    name='nome'
                                    label='Nome Fantasia'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.nome}
                                    onChange={formik.handleChange}
                                    error={formik.touched.nome && Boolean(formik.errors.nome)}
                                    helperText={formik.touched.nome && formik.errors.nome} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    id='idSegmento'
                                    name='idSegmento'
                                    label='Principal Segmento de Atuação'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.idSegmento}
                                    onChange={formik.handleChange}
                                    error={formik.touched.idSegmento && Boolean(formik.errors.idSegmento)}
                                    helperText={formik.touched.idSegmento && formik.errors.idSegmento}>
                                    <MenuItem value={0}>Selecione o segmento de atuação do negócio</MenuItem>
                                    {segmentos.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    id='codigoTipo'
                                    name='codigoTipo'
                                    label='Tipo de Pessoa'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.codigoTipo}
                                    onChange={formik.handleChange}
                                    error={formik.touched.codigoTipo && Boolean(formik.errors.codigoTipo)}
                                    helperText={formik.touched.codigoTipo && formik.errors.codigoTipo}>
                                    <MenuItem key='1' value={1}>Pessoa Física</MenuItem>
                                    <MenuItem key='2' value={2}>Pessoa Jurídica</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={formik.values.codigoTipo === 1 ? '999.999.999-99' : '99.999.999/9999-99'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.cpfOuCnpj}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='cpfOuCnpj'
                                        name='cpfOuCnpj'
                                        label='CPF/CNPJ'
                                        variant='filled'
                                        error={formik.touched.cpfOuCnpj && Boolean(formik.errors.cpfOuCnpj)}
                                        helperText={formik.touched.cpfOuCnpj && formik.errors.cpfOuCnpj}
                                    />}
                                </InputMask>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <TextField
                                    fullWidth
                                    id='email'
                                    name='email'
                                    label='E-mail'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'(99) 9999-9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.telefonePrincipal}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='telefonePrincipal'
                                        name='telefonePrincipal'
                                        label='Telefone Principal'
                                        variant='filled'
                                        error={formik.touched.telefonePrincipal && Boolean(formik.errors.telefonePrincipal)}
                                        helperText={formik.touched.telefonePrincipal && formik.errors.telefonePrincipal}
                                    />}
                                </InputMask>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'(99) 9999-9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.telefoneSecundario}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='telefoneSecundario'
                                        name='telefoneSecundario'
                                        label='Telefone Secundário'
                                        variant='filled'
                                        error={formik.touched.telefoneSecundario && Boolean(formik.errors.telefoneSecundario)}
                                        helperText={formik.touched.telefoneSecundario && formik.errors.telefoneSecundario}
                                    />}
                                </InputMask>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='contato'
                                    name='contato'
                                    label='Nome para Contato'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.contato}
                                    onChange={formik.handleChange}
                                    error={formik.touched.contato && Boolean(formik.errors.contato)}
                                    helperText={formik.touched.contato && formik.errors.contato}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id='instagram'
                                    name='instagram'
                                    label='Instagram'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.instagram}
                                    onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id='facebook'
                                    name='facebook'
                                    label='Facebook'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.facebook}
                                    onChange={formik.handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <InputMask
                                    mask='99.999-999'
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.cep}
                                    onChange={(value) => {
                                        formik.handleChange(value);
                                        handleCep(value);
                                    }}>
                                    {() => <TextField
                                        fullWidth
                                        id='cep'
                                        name='cep'
                                        label='CEP'
                                        variant='filled'
                                        error={formik.touched.cep && Boolean(formik.errors.cep)}
                                        helperText={formik.touched.cep && formik.errors.cep}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <TextField
                                    fullWidth
                                    id='logradouro'
                                    name='logradouro'
                                    label='Logradouro'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.logradouro}
                                    onChange={formik.handleChange}
                                    error={formik.touched.logradouro && Boolean(formik.errors.logradouro)}
                                    helperText={formik.touched.logradouro && formik.errors.logradouro}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id='numero'
                                    name='numero'
                                    label='Número'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.numero}
                                    onChange={formik.handleChange}
                                    error={formik.touched.numero && Boolean(formik.errors.numero)}
                                    helperText={formik.touched.numero && formik.errors.numero}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    id='complemento'
                                    name='complemento'
                                    label='Complemento'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.complemento}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='bairro'
                                    name='bairro'
                                    label='Bairro'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.bairro}
                                    onChange={formik.handleChange}
                                    error={formik.touched.bairro && Boolean(formik.errors.bairro)}
                                    helperText={formik.touched.bairro && formik.errors.bairro}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='cidade'
                                    name='cidade'
                                    label='Cidade'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.cidade}
                                    onChange={formik.handleChange}
                                    error={formik.touched.cidade && Boolean(formik.errors.cidade)}
                                    helperText={formik.touched.cidade && formik.errors.cidade}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='estado'
                                    name='estado'
                                    label='Estado'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.estado}
                                    onChange={formik.handleChange}
                                    error={formik.touched.estado && Boolean(formik.errors.estado)}
                                    helperText={formik.touched.estado && formik.errors.estado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    id='planoId'
                                    name='planoId'
                                    label='Plano'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.planoId}
                                    onChange={formik.handleChange}
                                    error={formik.touched.planoId && Boolean(formik.errors.planoId)}
                                    helperText={formik.touched.planoId && formik.errors.planoId}>
                                    <MenuItem value={0}>Selecione um Plano</MenuItem>
                                    {
                                        planosAtivos.map((option) => (
                                            <MenuItem key={option.id} value={option.id} >
                                                {option.nome}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>

                            {formik.values.id && formik.values.id > 0
                                ?
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            id='dataCadastro'
                                            name='dataCadastro'
                                            label='Data de Cadastro'
                                            variant='filled'
                                            disabled={true}
                                            value={formik.values.dataCadastro}
                                            onChange={formik.handleChange}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            id='stripeSellerAccountId'
                                            name='stripeSellerAccountId'
                                            label='Conta Stripe'
                                            variant='filled'
                                            disabled={true}
                                            value={formik.values.stripeSellerAccountId}
                                            onChange={formik.handleChange}
                                        />
                                    </Grid>
                                </>
                                :
                                null}

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch
                                        checked={formik.values.ativo}
                                        onChange={formik.handleChange}
                                        name="ativo"
                                        disabled={disableFields} />}
                                    label="Loja Ativa no App"
                                />
                            </Grid>

                        </Grid>
                        <Grid container pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                            <If test={!disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='secondary' variant='contained'>Salvar</Button>
                                </Grid>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href='/lojas'>Cancelar</Button>
                                </Grid>
                            </If>
                            <If test={disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button color='secondary' variant='contained' href='/lojas'>Voltar</Button>
                                </Grid>
                            </If>
                        </Grid>
                    </div>
                </form>
            </Grid>

            {/* O componente Loading fica ouvindo o estado isProcessing */}
            <LoadingOverlay
                open={isProcessing || processing}
                message={processingMessage}
            />

        </>
    )
}

const mapStateToProps = state => ({
    idDialogShow: state.dialog.idDialogShow,
    negocio: state.negocio.negocio,
    segmentos: state.negocio.segmentos,
    loading: state.negocio.loading,
    processing: state.negocio.processing,
    planosAtivos: state.negocio.planosAtivos,
    user: state.auth.user,
})
const mapDispatchToProps = dispatch => bindActionCreators({
    toggleShowDialog,
    addNegocio,
    updateNegocio,
    getNegocio,
    buscarSegmentosAtuacao,
    buscarPlanosAtivos
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(NegocioCadastro)